import { ArrowBendDownRight, Prohibit } from "@phosphor-icons/react"
import { ChevronDown, ChevronUp, X } from "lucide-react"
import { useEffect } from "react"

import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogTitle,
} from "@/components/ui/dialog"
import { cn } from "@/lib/utils"

import { formatRelative } from "./helpers"
import { StarRating } from "./star-rating"
import type { DashboardReview } from "./types"

// Read-only twin of ReviewDraftModal: opened from a Resolved row so the full
// review and its outcome (a posted response, or a note that it was dismissed)
// can be read in the same two-column layout, no matter how long either one is.
// Steps through the resolved list with the same chevrons + ↑/↓ + position
// counter, but with no editing. A dismissed review can be restored to pending.
export function PostedReviewModal({
  open,
  onOpenChange,
  review,
  index,
  total,
  processing,
  onPrev,
  onNext,
  onRestore,
}: {
  open: boolean
  onOpenChange: (v: boolean) => void
  review: DashboardReview | null
  index: number
  total: number
  processing: boolean
  onPrev: () => void
  onNext: () => void
  onRestore: (draftUid: string) => void
}) {
  const canPrev = index > 0
  const canNext = index < total - 1
  const isDismissed = review?.status === "dismissed"

  // ↑/↓ step through the resolved list, mirroring the draft modal.
  useEffect(() => {
    if (!open) return
    function onKey(e: KeyboardEvent) {
      if (e.key === "ArrowUp" && canPrev) {
        e.preventDefault()
        onPrev()
      } else if (e.key === "ArrowDown" && canNext) {
        e.preventDefault()
        onNext()
      }
    }
    window.addEventListener("keydown", onKey)
    return () => window.removeEventListener("keydown", onKey)
  }, [open, canPrev, canNext, onPrev, onNext])

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        showCloseButton={false}
        className="top-[8vh] flex h-[80vh] translate-y-0 flex-col gap-0 overflow-hidden p-0 sm:max-w-5xl"
      >
        {/* Header: title · position · prev/next · close */}
        <div className="flex items-center justify-between border-b px-5 py-3">
          <DialogTitle className="text-sm font-medium">
            {isDismissed ? "Dismissed review" : "Posted response"}
          </DialogTitle>
          <div className="flex items-center gap-1">
            {total > 0 && (
              <span className="text-muted-foreground mr-1 text-xs tabular-nums">
                {index + 1} of {total}
              </span>
            )}
            <NavButton label="Previous" disabled={!canPrev} onClick={onPrev}>
              <ChevronUp className="size-4" />
            </NavButton>
            <NavButton label="Next" disabled={!canNext} onClick={onNext}>
              <ChevronDown className="size-4" />
            </NavButton>
            <DialogClose className="text-muted-foreground hover:text-foreground hover:bg-muted ml-1 flex size-7 items-center justify-center rounded-md transition-colors">
              <X className="size-4" />
              <span className="sr-only">Close</span>
            </DialogClose>
          </div>
        </div>

        {review && (
          <>
            <div className="grid flex-1 grid-cols-2 divide-x overflow-hidden">
              {/* Left: the original review, top-aligned in a gray well. */}
              <div className="bg-muted/40 overflow-y-auto p-5">
                <div className="bg-background w-full space-y-2 rounded-lg border p-4 shadow-xs">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex min-w-0 items-center gap-2">
                      <p className="text-sm font-semibold">
                        {review.reviewer_name}
                      </p>
                      <StarRating rating={review.rating} />
                    </div>
                    <p className="text-muted-foreground shrink-0 text-xs">
                      {formatRelative(review.received_at)}
                    </p>
                  </div>
                  <p className="text-muted-foreground text-sm leading-relaxed">
                    {review.body}
                  </p>
                </div>
              </div>

              {/* Right: the outcome — the posted response, or the dismissed
                  draft with a note and a way to respond after all. */}
              <div className="bg-background flex flex-col overflow-hidden p-5">
                {isDismissed ? (
                  <>
                    <div className="text-muted-foreground mb-2 flex items-center gap-1.5">
                      <Prohibit className="size-4 shrink-0" />
                      <p className="text-sm font-medium">
                        Dismissed without a response
                      </p>
                    </div>
                    {review.dismissed_draft && (
                      <p className="text-muted-foreground/80 overflow-y-auto text-sm leading-relaxed whitespace-pre-wrap italic">
                        {review.dismissed_draft}
                      </p>
                    )}
                  </>
                ) : (
                  <>
                    <div className="mb-2 flex items-center gap-1.5">
                      <ArrowBendDownRight className="text-muted-foreground size-4" />
                      <p className="text-sm font-medium">Response</p>
                    </div>
                    <p className="overflow-y-auto text-sm leading-relaxed whitespace-pre-wrap">
                      {review.response}
                    </p>
                  </>
                )}
              </div>
            </div>

            {isDismissed && review.draft_uid && (
              <div className="flex items-center justify-end border-t px-5 py-4">
                <Button
                  size="sm"
                  variant="outline"
                  className="rounded-sm"
                  disabled={processing}
                  onClick={() => onRestore(review.draft_uid!)}
                >
                  Restore
                </Button>
              </div>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}

function NavButton({
  label,
  disabled,
  onClick,
  children,
}: {
  label: string
  disabled: boolean
  onClick: () => void
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
      className={cn(
        "flex size-7 items-center justify-center rounded-md transition-colors",
        disabled
          ? "text-muted-foreground/40"
          : "text-muted-foreground hover:text-foreground hover:bg-muted",
      )}
    >
      {children}
    </button>
  )
}
